/**
 * One error-reporting privacy policy for every tier (API, worker, both SPAs). This is a clinical
 * app: nothing that could carry patient data leaves the process — no request bodies, cookies,
 * auth headers or e-mails; only ids, roles and stacks.
 *
 * Structurally typed so kit stays dependency-free: any Sentry event shape fits.
 */
export interface ScrubbableEvent {
  request?: {
    data?: unknown;
    cookies?: unknown;
    headers?: Record<string, string> | undefined;
    query_string?: unknown;
  };
  user?: { id?: string } & Record<string, unknown>;
  exception?: { values?: Array<{ value?: string }> };
  message?: string;
}

const SENSITIVE_HEADERS = [
  "cookie",
  "authorization",
  "x-telegram-bot-api-secret-token",
  "x-forwarded-for",
];

/** URL query strings carry ids and search terms; keep the key, drop the value. */
export function redactQueryValues(text: string): string {
  return text.replace(/([?&][^=&\s]+)=([^&\s]*)/g, "$1=[redacted]");
}

/**
 * Drizzle wraps a failed query as `Failed query: <sql>\nparams: <values>`. The values are
 * whatever the app bound — patient names, phones, notes — so they must never reach a log line
 * or an error tracker. Used by the logger and by every `beforeSend`.
 */
export function redactBoundParams(message: string): string {
  return message.replace(/\nparams: [\s\S]*$/, "\nparams: [redacted]");
}

/** Both redactions, in the order that keeps each pattern intact. */
export function redactSensitiveText(text: string): string {
  return redactQueryValues(redactBoundParams(text));
}

/** `beforeSend`: strip anything that could carry clinical or personal data. */
export function scrubEvent<E extends object>(event: E): E {
  const view = event as ScrubbableEvent;
  if (view.request) {
    view.request.data = undefined;
    view.request.cookies = undefined;
    view.request.query_string = undefined;
    if (view.request.headers) {
      for (const name of Object.keys(view.request.headers)) {
        if (SENSITIVE_HEADERS.includes(name.toLowerCase())) delete view.request.headers[name];
      }
    }
  }
  if (view.user) view.user = { id: view.user.id };
  for (const exception of view.exception?.values ?? []) {
    if (exception.value) exception.value = redactSensitiveText(exception.value);
  }
  if (view.message) view.message = redactSensitiveText(view.message);
  return event;
}

/** Noise that is never a bug of ours. */
export const IGNORED_ERRORS = [
  "ResizeObserver loop",
  "AbortError",
  "Load failed",
  "NetworkError when attempting to fetch resource",
];

/** 1 in 10 requests gets a performance trace; errors are always sent. */
export const TRACES_SAMPLE_RATE = 0.1;

/**
 * Reporting is production-only by code: a DSN alone is not enough. Dev, CI and e2e therefore
 * never open a socket even if someone copies the DSN into an env file.
 */
export function isTelemetryActive(dsn: string | undefined, environment: string): boolean {
  return Boolean(dsn) && environment === "production";
}

/** "production" on Railway, "local" everywhere else (an explicit value always wins). */
export function resolveEnvironment(explicit?: string, railwayEnvironmentName?: string): string {
  return explicit || (railwayEnvironmentName ? "production" : "local");
}
